import { Paging } from '@Core';
import { BaseHttpController } from '@Core/controller/BaseHttpController';
import { AxiosInstance } from 'axios';
import { Camera, CameraPhotos, CameraWithInfo } from '../../model';
import {
    CameraStatisticsByServiceType,
    ICameraHttpController,
    IChangeCameraStatusProps,
    IGetAllCameraProps,
    IGetCameraProps,
    IGetCamerasWithoutSIM,
    IGetCameraTaskProps,
    IGetListCamera,
    IGetPMC8266CameraProps,
    IGetPTZCameraProps,
    IGetSP111CameraProps,
    ISetCameraPhoto,
    ISwapMacCamera,
    IUpdateConfigEnableGroupMatching,
    IUpdateConfigEnableGroupMatchingResponse,
    IUpdateConfigIsVerificationsEnabled,
    IUpdateConfigIsVerificationsEnabledResponse,
    IUpsertCameraProps,
} from '../ICameraHttpController';

export class CameraHttpController extends BaseHttpController<Camera> implements ICameraHttpController {
    constructor(serviceUrl: string, client: AxiosInstance) {
        super(serviceUrl, 'camera', client);
    }

    async wakeCameraByManually(id: number) {
        return await this.doGet({ path: `wake-up-manually/${id}` }).then((res) => res.data);
    }

    async upsertWithSolarCamera(props: IUpsertCameraProps): Promise<CameraWithInfo> {
        return await this.doPost({ path: 'upsert-with-solar-camera', body: props }).then((res) => res.data);
    }

    async upsertWithPTZCamera(props: IUpsertCameraProps): Promise<CameraWithInfo> {
        return await this.doPost({ path: 'upsert-with-ptz-camera', body: props }).then((res) => res.data);
    }

    async upsertWithSP111Camera(props: IUpsertCameraProps): Promise<CameraWithInfo> {
        return await this.doPost({ path: 'upsert-with-sp111-camera', body: props }).then((res) => res.data);
    }

    async changeStatus(props: IChangeCameraStatusProps): Promise<Camera> {
        return await this.doPost({ path: 'change-status', body: props }).then((res) => res.data);
    }

    async list(params: IGetCameraProps): Promise<Paging<CameraWithInfo>> {
        return await this.doPost({ path: 'filter', body: params }).then((res) => res.data);
    }

    async listPTZCamera(props: IGetPTZCameraProps): Promise<Paging<CameraWithInfo>> {
        return await this.doPost({ path: 'list-ptz-camera', body: props }).then((res) => res.data);
    }

    async listSP111Camera(props: IGetSP111CameraProps): Promise<Paging<CameraWithInfo>> {
        return await this.doPost({ path: 'list-sp111-camera', body: props }).then((res) => res.data);
    }

    async listAllCameras(props: IGetAllCameraProps): Promise<Paging<CameraWithInfo>> {
        return await this.doPost({ path: 'list-all-cameras', body: props }).then((res) => res.data);
    }

    async listCameraAbleToCreateTask(props: IGetCameraTaskProps): Promise<Paging<CameraWithInfo>> {
        return await this.doPost({ path: 'list-camera-able-to-create-task', body: props }).then((res) => res.data);
    }

    async get(id: string): Promise<CameraWithInfo> {
        return (await super.get(id)) as CameraWithInfo;
    }

    async getLatestCameraPhoto(cameraId: number): Promise<CameraPhotos> {
        return await this.doGet({ path: `latest-photo/${cameraId}` }).then((res) => res.data);
    }

    async checkANPRCameraExisted(anprCameraId: number): Promise<boolean> {
        return await this.doGet({ path: `duplicate-anpr-camera/${anprCameraId}` }).then((res) => res.data);
    }

    async updateConfigIsVerificationsEnabled(
        props: IUpdateConfigIsVerificationsEnabled
    ): Promise<IUpdateConfigIsVerificationsEnabledResponse> {
        return await this.doPost({ path: `update-config/IsVerificationsEnabled`, body: props }).then((res) => res.data);
    }

    async updateConfigEnableGroupMatching(
        props: IUpdateConfigEnableGroupMatching
    ): Promise<IUpdateConfigEnableGroupMatchingResponse> {
        return await this.doPost({ path: `update-config/EnableGroupMatching`, body: props }).then((res) => res.data);
    }

    async checkIsVRNOfPO(vrn: string): Promise<boolean> {
        return await this.doGet({ path: `check-po-vrn/${vrn}` }).then((res) => res.data);
    }

    async getListMacCameraInActive(): Promise<string[]> {
        return await this.doGet({ path: `get-list-mac-camera-inactive` }).then((res) => res.data);
    }

    async setCameraPhoto(props: ISetCameraPhoto): Promise<CameraPhotos> {
        return await this.doPost({ path: `set-camera-photo`, body: props }).then((res) => res.data);
    }

    async swapMacCamera(props: ISwapMacCamera): Promise<string> {
        return await this.doPost({ path: 'swap-mac-camera', body: props }).then((res) => res.data);
    }

    async getCameraStatisticsByServiceType(): Promise<CameraStatisticsByServiceType> {
        return await this.doPost({ path: 'camera-statistics-by-serivce-type' }).then((res) => res.data);
    }

    async upsertWithPMC8266Camera(props: IUpsertCameraProps): Promise<CameraWithInfo> {
        return await this.doPost({ path: 'upsert-with-pmc8266-camera', body: props }).then((res) => res.data);
    }

    async listPMC8266Camera(props: IGetPMC8266CameraProps): Promise<Paging<CameraWithInfo>> {
        return await this.doPost({ path: 'list-pmc8266-camera', body: props }).then((res) => res.data);
    }

    async getLatestCameraPhotoPMC8266(cameraId: number): Promise<CameraPhotos> {
        return await this.doGet({ path: `latest-photo-pmc-8266/${cameraId}` }).then((res) => res.data);
    }

    async getListCamera(props: IGetListCamera): Promise<Paging<Camera>> {
        return await this.doPost({ path: 'get-list-camera', body: props }).then((res) => res.data);
    }

    async getCamerasWithoutSIM(props: IGetCamerasWithoutSIM): Promise<Paging<Camera>> {
        return await this.doPost({ path: 'get-camera-without-sim', body: props }).then((res) => res.data);
    }

    async getLensCapacity(): Promise<string[]> {
        return await this.doGet({ path: 'camera-info/lens-capacities' }).then((res) => res.data);
    }
}
